import { Button, Form } from "antd";
import { useEffect, useRef, useState } from "react";
import CloudArrowUpIcon from "../../assets/icons/CloudArrowUpIcon";
import { useUploadFileMutation } from "../../features/Auth/auth.api";
import { toast } from "../../lib/toast";

// Extract filename from URL
export const getFileNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "";
  } catch {
    return url.split("/").pop() || "";
  }
};

// Split into [basename, extension] with truncation
const renderFileName = (fileName: string): [string, string] => {
  if (!fileName) return ["", ""];

  const parts = fileName.split(".");
  const ext = parts.pop() || "";
  const base = parts.join(".");

  return [base, `.${ext}`];
};

type Props = {
  errorMessage?: string;
  form: any;
  name: string;
  disabled?: boolean;
  accept?: string;
  size?: "large" | "middle" | "small";
  placeholder?: string;
  description?: string;
  onChange?: (url: string | null) => void;
  value?: string;
  maxSize?: number; // ✅ in MB
};

export default function FileInput({
  errorMessage = "File upload failed",
  form,
  name,
  disabled,
  accept = ".png,.jpeg,.svg,.jpg",
  size = "large",
  placeholder = "No file chosen",
  description,
  onChange,
  value,
  maxSize = 5, // ✅ default 5 MB
}: Props) {
  const [fileName, setFileName] = useState("");
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [basename, ext] = renderFileName(fileName);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ✅ convert MB → bytes and check
    const maxBytes = maxSize * 1024 * 1024;
    if (file.size > maxBytes) {
      toast("error", `File size should not exceed ${maxSize} MB`);
      onChange?.(null);
      setFileName("");
      e.target.value = ""; // clear file input
      return;
    }
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadFile(formData).unwrap();
      const fileUrl = res?.Location;

      setFileName(getFileNameFromUrl(fileUrl));
      form.setFieldValue(name, fileUrl);
      onChange?.(fileUrl);
    } catch (err) {
      toast("error", errorMessage);
      onChange?.(null);
      setFileName("");
    }
  };
  const watchedFile = Form.useWatch(name, form);

  useEffect(() => {
    setFileName(getFileNameFromUrl(watchedFile));
  }, [watchedFile]);

  useEffect(() => {
    if (value) {
      setFileName(getFileNameFromUrl(value));
    }
  }, [value]);

  return (
    <Form.Item name={name} className="mb-0">
      <div className="flex flex-col gap-2">
        {/* Hidden input */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="!hidden"
          disabled={disabled || isLoading}
          onChange={handleFileChange}
        />

        {/* Custom trigger button */}
        <Button
          className={`button-white-bordered justify-start ${
            size === "large" ? "!h-[46px]" : ""
          }`}
          block
          size={size}
          disabled={disabled || isLoading}
          onClick={() => inputRef.current?.click()}
        >
          <span className="flex items-center pl-3">
            <CloudArrowUpIcon />
          </span>
          <div className="flex-1 flex items-center gap-1 text-sm font-medium text-gray-2">
            {isLoading ? (
              "Uploading..."
            ) : fileName ? (
              <span className="flex">
                <span className="truncate max-w-[55%]">{basename}</span>
                <span className="text-gray-400">{ext}</span>
              </span>
            ) : (
              placeholder
            )}
          </div>
        </Button>

        {description && (
          <div className="mt-2 text-xs text-theme-gray-50">{description}</div>
        )}
      </div>
    </Form.Item>
  );
}
